/**
 * Utility functions for working with API responses
 */

/**
 * Processes an API response to extract an array based on common patterns
 * @param {any} response - The API response to process
 * @param {string} entityType - The type of entity (for logging purposes)
 * @param {string} arrayKey - Optional key to look for array in response object
 * @param {function} filterFn - Optional function to filter items when extracting from object
 * @returns {Array} - The extracted array or empty array if extraction failed
 */
export const extractArrayFromResponse = (response, entityType, arrayKey = null, filterFn = null) => {
  
  // Case 1: Response is already an array
  if (Array.isArray(response)) {
    return response;
  }
  
  // Case 2: Response with standard REST API format (data property containing array)
  if (response && typeof response === 'object' && response.data && Array.isArray(response.data)) {
    return response.data;
  }
  
  // Case 3: Response is an object with the specified array key
  if (response && typeof response === 'object' && arrayKey && Array.isArray(response[arrayKey])) {
    return response[arrayKey];
  }
  
  // Case 4: Response is an object that needs to be converted to array
  if (response && typeof response === 'object') {
    // Get all values that are objects from the response
    const valueArray = Object.values(response).filter(item => 
      item && typeof item === 'object'
    );
    
    // If a filter function is provided, further filter the values
    const filteredArray = filterFn ? valueArray.filter(filterFn) : valueArray;
    
    if (filteredArray.length > 0) {
      return filteredArray;
    }
  }
  
  // Case 5: Response is invalid or empty
  console.error(`Could not extract ${entityType} array from response:`, response);
  return [];
};

/**
 * Common filter functions for different entity types
 */
export const entityFilters = {
  users: item => 'email' in item || 'name' in item,
  events: item => 'title' in item || 'date' in item,
  stories: item => 'title' in item || 'content' in item,
  banners: item => 'image' in item || 'imageUrl' in item
};
